import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./pages/AuthCallback";
import Landing from "./pages/Landing";
import Onboarding from "./pages/Onboarding";
import Layout from "./components/Layout";
import Submit from "./pages/Submit";
import Queue from "./pages/Queue";
import Compose from "./pages/Compose";
import Inbox from "./pages/Inbox";
import Earnings from "./pages/Earnings";
import Feed from "./pages/Feed";
import Admin from "./pages/Admin";
import Reveal from "./pages/Reveal";
import Settings from "./pages/Settings";
import CreateTask from "./pages/CreateTask";
import ReviewQueue from "./pages/ReviewQueue";
import ReviewerProfile from "./pages/ReviewerProfile";
import MyTasks from "./pages/MyTasks";
import ResultPublic from "./pages/ResultPublic";
import HotTakes from "./pages/HotTakes";
import BattleArena from "./pages/BattleArena";
import BattleJoin from "./pages/BattleJoin";
import BattlePublic from "./pages/BattlePublic";
import RoasterProfilePage from "./pages/RoasterProfilePage";
import Arena from "./pages/Arena";
import BrandChallenge from "./pages/BrandChallenge";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/app/submit" replace />;
  return <Layout>{children}</Layout>;
}

function OnboardingGate() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (user.onboarded) return <Navigate to="/app/submit" replace />;
  return <Onboarding />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reveal/:token" element={<Reveal />} />
      <Route path="/result/:token" element={<ResultPublic />} />
      <Route path="/hot-takes" element={<HotTakes />} />
      <Route path="/battle/join/:token" element={<BattleJoin />} />
      <Route path="/battle/:token" element={<BattlePublic />} />
      <Route path="/roaster/:id" element={<RoasterProfilePage />} />
      <Route path="/labs/:slug" element={<BrandChallenge />} />
      <Route path="/app/battles" element={<Protected><BattleArena /></Protected>} />
      <Route path="/app/arena" element={<Protected><Arena /></Protected>} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/app/create" element={<Protected><CreateTask /></Protected>} />
      <Route path="/app/review" element={<Protected><ReviewQueue /></Protected>} />
      <Route path="/app/reviewer-profile" element={<Protected><ReviewerProfile /></Protected>} />
      <Route path="/app/tasks" element={<Protected><MyTasks /></Protected>} />
      <Route path="/app/submit" element={<Protected><Submit /></Protected>} />
      <Route path="/app/queue" element={<Protected><Queue /></Protected>} />
      <Route path="/app/compose/:id" element={<Protected><Compose /></Protected>} />
      <Route path="/app/inbox" element={<Protected><Inbox /></Protected>} />
      <Route path="/app/earnings" element={<Protected><Earnings /></Protected>} />
      <Route path="/app/feed" element={<Protected><Feed /></Protected>} />
      <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/app/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="bottom-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
